import { DraggableSidebar } from "../../controls";
import { useArsListing } from "./ArsListingContextProvider";
import { DatasetSelect } from "./DatasetSelect";
import { type ArsListingVariable } from "./interface";
import { RenderVariableSidebarItem } from "./RenderVariableSidebarItem";
import { VariableFilter } from "./VariableFilter";

/**
 * Renders the ArsListing draggable variable sidebar using state from ArsListing context.
 *
 * @returns Sidebar container with dataset selector, variable filter input, and draggable variable items
 */
export const ArsListingDraggableSidebar = (): React.ReactNode => {
  const { state } = useArsListing();

  const selectedDataset = (state?.settings.datasets ?? []).find(
    (dataset) => dataset.id === state?.settings.currentDatasetId,
  );

  const filterText = (state?.settings.variableFilter ?? "").toLowerCase();

  const sidebarItems =
    selectedDataset?.clinicalVariables
      .filter(
        (variable) =>
          filterText === "" ||
          variable.name.toLowerCase().includes(filterText) ||
          variable.description.toLowerCase().includes(filterText),
      )
      .map((variable) => ({
        id: `ars-listing-sidebar-item-${String(variable.id)}`,
        label: variable.description.trim() === "" ? variable.name : variable.description.trim(),
        data: {
          ...variable,
          datasetName: selectedDataset.name,
        },
      })) ?? [];

  return !state ? null : (
    <div className="d-flex justify-content-end align-items-start">
      <DraggableSidebar<ArsListingVariable>
        show={state.settings.showVariableList}
        side="right"
        width={state.settings.sidebarWidth}
        bodyLevel={true}
        pushContent={true}
        items={sidebarItems}
        header={
          <div className="d-flex flex-column gap-2">
            <DatasetSelect />
            <VariableFilter />
          </div>
        }
        multiSelect={true}
        renderItem={RenderVariableSidebarItem}
      />
    </div>
  );
};

ArsListingDraggableSidebar.displayName = "ArsListingDraggableSidebar";
